import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import './FeeOverride.css';

// Use relative URLs to leverage the proxy configured in package.json
// This ensures cookies are sent correctly (same-origin)
const API_BASE_URL = '/api';

// Currency symbols
const CURRENCY_OPTIONS = [
  { code: 'USD', symbol: '$', label: 'USD ($)' },
  { code: 'INR', symbol: '\u20B9', label: 'INR (\u20B9)' },
  { code: 'GBP', symbol: '\u00A3', label: 'GBP (\u00A3)' },
  { code: 'EUR', symbol: '\u20AC', label: 'EUR (\u20AC)' },
  { code: 'AED', symbol: 'AED', label: 'AED' },
  { code: 'CAD', symbol: 'C$', label: 'CAD (C$)' },
  { code: 'AUD', symbol: 'A$', label: 'AUD (A$)' },
  { code: 'JPY', symbol: '\u00A5', label: 'JPY (\u00A5)' },
  { code: 'SGD', symbol: 'S$', label: 'SGD (S$)' },
  { code: 'SAR', symbol: 'SAR', label: 'SAR' },
];

// Mock data - used when API is not available
const mockDestinations = [
  {
    destination_country_id: 1,
    destination_country_name: 'United Kingdom',
    base_cost_starts_from: 120,
    partner_premium: 45,
    currency: 'USD'
  },
  {
    destination_country_id: 2,
    destination_country_name: 'Japan',
    base_cost_starts_from: 80,
    partner_premium: 25,
    currency: 'USD'
  },
  {
    destination_country_id: 3,
    destination_country_name: 'Canada',
    base_cost_starts_from: 150,
    partner_premium: 60,
    currency: 'USD'
  },
  {
    destination_country_id: 4,
    destination_country_name: 'USA',
    base_cost_starts_from: 160,
    partner_premium: 0,
    currency: 'USD'
  },
  {
    destination_country_id: 5,
    destination_country_name: 'Schengen Area',
    base_cost_starts_from: 95,
    partner_premium: 0,
    currency: 'USD'
  },
  {
    destination_country_id: 6,
    destination_country_name: 'Australia',
    base_cost_starts_from: 110,
    partner_premium: 0,
    currency: 'USD'
  }
];

const FeeOverride = () => {
  const { user } = useAuth();
  const [destinations, setDestinations] = useState([]);
  const [editedPremiums, setEditedPremiums] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [useMockData, setUseMockData] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [exchangeRates, setExchangeRates] = useState(null);

  // Fetch exchange rates
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/agency/exchange-rates`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          if (data.success) setExchangeRates(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch exchange rates:', err);
      }
    };
    fetchRates();
  }, []);

  // Currency conversion helper (display only, USD is source of truth)
  const convert = (usdAmount) => {
    if (selectedCurrency === 'USD' || !exchangeRates?.rates) return usdAmount;
    const rate = exchangeRates.rates[selectedCurrency];
    if (!rate) return usdAmount;
    return Math.round(usdAmount * rate * 100) / 100;
  };

  const getSymbol = () => {
    const opt = CURRENCY_OPTIONS.find(c => c.code === selectedCurrency);
    return opt ? opt.symbol : '$';
  };

  // Fetch fee overrides data
  const fetchFeeOverrides = useCallback(async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API_BASE_URL}/agency/fee-overrides`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.destinations) {
          setDestinations(data.data.destinations);
          // Initialize edited premiums with current values
          const premiums = {};
          data.data.destinations.forEach(dest => {
            premiums[dest.destination_country_id] = dest.partner_premium;
          });
          setEditedPremiums(premiums);
          setUseMockData(false);
        } else {
          // API returned but no destinations - use mock data
          loadMockData();
        }
      } else {
        // API error - use mock data
        loadMockData();
      }
    } catch (error) {
      console.error('Error fetching fee overrides:', error);
      // Network error - use mock data
      loadMockData();
    } finally {
      setLoading(false);
    }
  }, []);

  // Load mock data fallback
  const loadMockData = () => {
    setDestinations(mockDestinations);
    const premiums = {};
    mockDestinations.forEach(dest => {
      premiums[dest.destination_country_id] = dest.partner_premium;
    });
    setEditedPremiums(premiums);
    setUseMockData(true);
  };

  useEffect(() => {
    fetchFeeOverrides();
  }, [fetchFeeOverrides]);

  // Handle premium input change
  const handlePremiumChange = (destinationId, value) => {
    // Allow empty string or valid numbers
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setEditedPremiums(prev => ({
        ...prev,
        [destinationId]: value
      }));
    }
  };

  // Check if any premium has changed
  const hasAnyChanges = () => {
    return destinations.some(dest => {
      const currentPremium = parseFloat(editedPremiums[dest.destination_country_id]) || 0;
      return currentPremium !== dest.partner_premium;
    });
  };

  // Save all changed premiums
  const saveAllPremiums = async () => {
    try {
      setSaving(true);

      // Find all changed destinations
      const changedDestinations = destinations.filter(dest => {
        const currentPremium = parseFloat(editedPremiums[dest.destination_country_id]) || 0;
        return currentPremium !== dest.partner_premium;
      });

      if (!useMockData) {
        // Save each changed premium
        for (const dest of changedDestinations) {
          const premium = parseFloat(editedPremiums[dest.destination_country_id]) || 0;
          const response = await fetch(`${API_BASE_URL}/agency/fee-overrides/${dest.destination_country_id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ partner_premium: premium })
          });

          if (!response.ok) {
            throw new Error('Failed to save premium');
          }
        }
      } else {
        // Simulate API delay for mock data
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Update local state with new values
      setDestinations(prev => prev.map(dest => ({
        ...dest,
        partner_premium: parseFloat(editedPremiums[dest.destination_country_id]) || 0
      })));

      setSaving(false);
    } catch (error) {
      console.error('Error saving premiums:', error);
      setSaving(false);
    }
  };

  // Calculate final quote
  const calculateFinalQuote = (baseCost, premium) => {
    const premiumValue = parseFloat(premium) || 0;
    return baseCost + premiumValue;
  };

  return (
    <div className="page-layout">
      <Sidebar />

      <main className="page-main">
        <div className="fee-override-header">
          <div className="header-left">
            <h1 className="page-title">FEE CONTROL PANEL</h1>
            <p className="page-subtitle">Dynamically override your partner premiums per destination.</p>
          </div>
          <div className="header-right">
            <div className="currency-selector">
              <label className="currency-selector-label">DISPLAY IN</label>
              <select
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                className="currency-select"
              >
                {CURRENCY_OPTIONS.map(c => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
            </div>
            <button
              className={`save-btn ${hasAnyChanges() ? 'has-changes' : ''}`}
              onClick={saveAllPremiums}
              disabled={!hasAnyChanges() || saving}
            >
              {saving ? 'SAVING...' : 'SAVE CHANGES'}
            </button>
          </div>
        </div>

        <div className="fee-override-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading destinations...</p>
            </div>
          ) : destinations.length === 0 ? (
            <div className="empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
              <p>No destinations configured</p>
              <span className="empty-subtitle">Contact admin to set up visa pricing</span>
            </div>
          ) : (
            <div className="destinations-grid">
              {destinations.map(destination => (
                <div key={destination.destination_country_id} className="destination-card">
                  <div className="card-corner-accent"></div>
                  <div className="card-header">
                    <h3 className="destination-name">{destination.destination_country_name}</h3>
                  </div>

                  <div className="card-body">
                    <div className="price-row base-cost-row">
                      <span className="price-label">BASE GOV/API COST</span>
                      <span className="price-value">{getSymbol()}{convert(destination.base_cost_starts_from)}</span>
                    </div>

                    <div className="price-row premium-row">
                      <span className="price-label">PARTNER PREMIUM</span>
                      <div className="premium-input-wrapper">
                        <span className="currency-symbol">$</span>
                        <input
                          type="text"
                          className="premium-input"
                          value={editedPremiums[destination.destination_country_id] ?? ''}
                          onChange={(e) => handlePremiumChange(destination.destination_country_id, e.target.value)}
                          placeholder="0"
                        />
                      </div>
                      {selectedCurrency !== 'USD' && exchangeRates && (
                        <span className="converted-amount">
                          {'\u2248'} {getSymbol()}{convert(parseFloat(editedPremiums[destination.destination_country_id]) || 0)}
                        </span>
                      )}
                    </div>

                    <div className="premium-progress-bar">
                      <div
                        className="premium-progress-fill"
                        style={{
                          width: `${Math.min(100, ((parseFloat(editedPremiums[destination.destination_country_id]) || 0) / destination.base_cost_starts_from) * 100)}%`
                        }}
                      />
                    </div>

                    <div className="final-quote-section">
                      <span className="final-quote-label">FINAL QUOTE</span>
                      <div className="final-quote-right">
                        <span className="final-quote-value">
                          {getSymbol()}{convert(calculateFinalQuote(
                            destination.base_cost_starts_from,
                            editedPremiums[destination.destination_country_id]
                          ))}
                        </span>
                        <span className="tax-note">TAX INCLUSIVE</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default FeeOverride;
